// src/lib/validation.ts
// Schémas Zod réutilisables côté client. Synchronisés avec les schémas backend.
import { z } from 'zod';

export const loginSchema = z.object({
  email: z.string().min(1, 'Email requis').email('Email invalide'),
  password: z.string().min(1, 'Mot de passe requis')
});
export type LoginInput = z.infer<typeof loginSchema>;

export const registerSchema = z.object({
  email: z.string().min(1, 'Email requis').email('Email invalide'),
  password: z
    .string()
    .min(8, 'Au moins 8 caractères')
    .max(128, 'Mot de passe trop long'),
  firstName: z.string().min(1, 'Prénom requis').max(80),
  lastName: z.string().min(1, 'Nom requis').max(80),
  phoneNumber: z
    .string()
    .min(5, 'Téléphone trop court')
    .max(30)
    .regex(/^[+0-9\s().-]+$/, 'Format de téléphone invalide'),
  role: z.enum(['client', 'deliverer', 'business']).default('client')
});
export type RegisterInput = z.infer<typeof registerSchema>;

const latitude = z.coerce.number().min(-90, 'Latitude entre -90 et 90').max(90, 'Latitude entre -90 et 90');
const longitude = z.coerce.number().min(-180, 'Longitude entre -180 et 180').max(180, 'Longitude entre -180 et 180');

export const createOrderSchema = z.object({
  delivererId: z.string().uuid('Livreur invalide'),
  pickupAddress: z.string().min(3, 'Adresse de prise en charge trop courte').max(500),
  pickupLatitude: latitude,
  pickupLongitude: longitude,
  deliveryAddress: z.string().min(3, 'Adresse de livraison trop courte').max(500),
  deliveryLatitude: latitude,
  deliveryLongitude: longitude,
  recipientName: z.string().min(1, 'Nom du destinataire requis').max(200),
  recipientPhone: z.string().min(5, 'Téléphone trop court').max(30),
  recipientEmail: z.string().email('Email invalide').optional().or(z.literal('').transform(() => undefined)),
  senderName: z.string().min(1, 'Nom de l\'expéditeur requis').max(200),
  senderPhone: z.string().min(5, 'Téléphone trop court').max(30),
  senderEmail: z.string().email('Email invalide').optional().or(z.literal('').transform(() => undefined)),
  weight: z.coerce.number().positive('Poids > 0').max(1000).optional(),
  volume: z.coerce.number().positive('Volume > 0').max(100).optional(),
  notes: z.string().max(2000).optional(),
  paymentMethod: z.enum(['cash', 'credit_card', 'mobile_money', 'bank_transfer', 'platform']).optional(),
  urgency: z.boolean().optional()
});
export type CreateOrderInputValidated = z.infer<typeof createOrderSchema>;

/**
 * Helper : retourne soit `{ ok: true, data }`, soit `{ ok: false, errors }` où
 * `errors` est un dict champ → message d'erreur (pratique pour afficher sous chaque input).
 */
export const validate = <T>(schema: z.ZodType<T>, input: unknown): {
  ok: true; data: T;
} | {
  ok: false; errors: Record<string, string>;
} => {
  const res = schema.safeParse(input);
  if (res.success) return { ok: true, data: res.data };
  const errors: Record<string, string> = {};
  for (const issue of res.error.issues) {
    const key = issue.path.join('.');
    if (!errors[key]) errors[key] = issue.message;
  }
  return { ok: false, errors };
};
